<script lang="ts" setup>
import { VideoClipper } from "@pixellot/vue-player";
import type { VideoJsPlayer as IPixellotPlayer } from "@pixellot/web-sdk";
import type { IPlayerState } from "@pixellot/vue-player/src/util/state";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import useEventStore from "~/stores/event";

export interface ClipperProps {
  start: number;
  end: number;
}

const { t } = useI18n();
const color = useColorMode();
const props = defineProps<ClipperProps>();
const eventStore = useEventStore();

const spriteMeta = () => {
  // TODO: until BE will implement thumbnail preview.
  const breakpoints = useBreakpoints(breakpointsTailwind);

  let size = "large";
  let width = 120;
  const height = 64;

  if (breakpoints.between("md", "xl").value) {
    size = "medium";
    width = 100;
  } else if (breakpoints.greater("xl").value) {
    width = 100;
  }

  const url = `/sprite_${color.value}_${size}.png`;

  return {
    url,
    width,
    height,
  };
};

const videoClipperRef = ref<InstanceType<typeof VideoClipper> | null>(null);
const selectedRange = ref<readonly [number, number]>();

const spriteThumbnailsDefaultOptions = ref({
  ...spriteMeta(),
  rows: 1,
  cells: 1,
});

const player = computed<IPixellotPlayer | null>(() => eventStore.player ? eventStore.player : null);
const playerState = computed<IPlayerState | null>(() => eventStore.playerState ? eventStore.playerState : null);
const selectedDuration = computed(() => {
  if (!videoClipperRef.value || !videoClipperRef.value.selectedRange || !eventStore.player)
    return 0;

  const [rangeStart, rangeEnd] = videoClipperRef.value.selectedRange;
  const duration = rangeEnd - rangeStart;

  return Math.ceil(duration);
});

watch(() => videoClipperRef.value?.selectedRange, (range) => {
  if (!range)
    return;
  nextTick(() => {
    selectedRange.value = range;
  });
});

onMounted(() => {
  nextTick(() => {
    videoClipperRef.value?.setRange(props.start, props.end);
  });
});

onMounted(() => {
  player.value?.spriteThumbnails().updateOptions(spriteThumbnailsDefaultOptions.value);
});
defineExpose({ selectedRange });
</script>

<template>
  <ClientOnly>
    <Teleport to="#player-bottom">
      <div v-if="eventStore.tags.isCreatingTag || eventStore.isCreatingClip" class="flex flex-col background-main">
        <div class="flex items-center text-secondary justify-center my-4 gap-4">
          <div class="border min-w-[5rem] px-4 py-1 rounded border-[var(--base-card-bgSecondaryColor)]">
            {{ formatTime(props.start) }}
          </div>
          <span>-</span>
          <div class="border min-w-[5rem] px-4 py-1 rounded border-[var(--base-card-bgSecondaryColor)]">
            {{ formatTime(props.end) }}
          </div>
        </div>

        <VideoClipper
          v-if="player && playerState"
          ref="videoClipperRef"
          :player="player"
          :player-state="playerState"
        />

        <div class="text-center text-secondary font-medium text-sm my-4">
          {{ selectedDuration }} {{ t('labels.seconds', 0) }}
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>
